import { createContext, useContext, useEffect, useRef } from 'react';
import type { MediaLibraryTheme } from '../types';

const ThemeContext = createContext<MediaLibraryTheme>('plain');

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({
  children,
  theme,
  className,
}: {
  children: React.ReactNode;
  theme: MediaLibraryTheme;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // Floating content (zpr-dropdown, zpr-modal) portals itself to the shadow
  // ROOT — above this div, which would put it outside the theme scope. The
  // host is the one element that is an ancestor of everything in the shadow
  // tree, portals included, so when we render inside a shadow root the scope
  // attribute must live there (matched via `:host([data-zpr-theme])` in
  // @zappar/zpr-styles' _scope.scss).
  useEffect(() => {
    const root = ref.current?.getRootNode();
    if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
      root.host.dataset.zprTheme = theme;
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={theme}>
      <div ref={ref} className={className} data-zpr-theme={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};
